import { splitEncoding } from '@contentful/live-preview';
// biome-ignore lint/style/useNodejsImportProtocol: Automated migration to Biome
import type { ParsedUrlQuery } from 'querystring';

import { CAMP_ID as ECOMM_CAMP_ID } from '@/constants/baseUrls';

// signle source of truth
enum ALLOWED_ATTRIBUTION_PARAMS {
  CAMPAIGN = 'campaign',
  CAMPID = 'campid',
  CRID = 'crid',
  GCLID = 'gclid',
  MKWID = 'mkwid',
  MP_KW = 'mp_kw',
  PDV = 'pdv',
  UTM_ADGROUP = 'utm_adgroup',
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_CONTENT = 'utm_content',
  UTM_MATCH = 'utm_match',
  UTM_MEDIUM = 'utm_medium',
  UTM_REFERRER = 'utm_referrer',
  UTM_SITE = 'utm_site',
  UTM_SOURCE = 'utm_source',
  UTM_TERM = 'utm_term',
  EXPERIMENT_ID = 'experiment_id',
  S2 = 's2',
  FBCLID = 'fbclid', // Meta
  TBLCI = 'tblci', // Taboola
  MSCLKID = 'msclkid', // Bing
  DICBO = 'dicbo', // Outbrain
}
export type AllowedAttributionParams = Partial<
  Record<ALLOWED_ATTRIBUTION_PARAMS, string>
>;
const allowedAttributionParams = Object.values(ALLOWED_ATTRIBUTION_PARAMS);

function isAllowedParam(name: string) {
  return Object.values(allowedAttributionParams).some((p) => name === p);
}

type getCampaignIdProps = {
  overrideCampaignId?: boolean;
  isEcomm?: boolean;
  contentfulCampaignId?: string;
};

export const getCampaignID = ({
  overrideCampaignId = false,
  isEcomm = false,
  contentfulCampaignId,
}: getCampaignIdProps): string => {
  const sessionParams = getAttributionParams();
  const { cleaned: cleanCfCampId } = splitEncoding(contentfulCampaignId || '');

  // Check URL query param campid as fallback if session storage is not set properly
  const queryParams = new URLSearchParams(window.location.search);
  const urlCampId = queryParams.get('campid');

  // P1. if isEcomm, we always use ECOMM_CAMP_ID.
  if (isEcomm) return ECOMM_CAMP_ID;
  // P2. if overrideCampaignId is true, use contentfulCampaignId if available, else use ECOMM_CAMP_ID as fallback
  if (overrideCampaignId) return cleanCfCampId || ECOMM_CAMP_ID;
  // Else, use session campid as P1, contentfulCampaignId as P2, current URL campid as P3, and ECOMM_CAMP_ID as fallback.
  return sessionParams.campid || cleanCfCampId || urlCampId || ECOMM_CAMP_ID;
};

export const captureAttributionParams = (query: ParsedUrlQuery) => {
  Object.entries(query).forEach(([key, value]) => {
    const isValidQueryValue = typeof value === 'string';

    if (isValidQueryValue && isAllowedParam(key)) {
      sessionStorage.setItem(key, value);
    }
  });
};

export const getAttributionParams = (): AllowedAttributionParams => {
  const utmParams: AllowedAttributionParams = {};

  Object.values(allowedAttributionParams).forEach((utmParam) => {
    const sessionValue = sessionStorage.getItem(utmParam);

    if (sessionValue) utmParams[utmParam] = sessionValue;
  });

  return utmParams;
};
